import { useState } from 'react';
import { T, useGT, Var } from 'gt-react';
import { ArrowsClockwiseIcon } from '@phosphor-icons/react';
import { useAllAgentDefinitions } from 'src/api/rpc/hooks/agent-definitions/useAgentDefinitions';
import { SingleSelect } from 'src/components/common/SingleSelect';
import { Button } from 'src/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import { Label } from 'src/components/ui/label';
import type { SingleSelectOption } from 'src/components/common/SingleSelect';
import { buildReturnAgentOptions } from './taskQueueReassign';

/**
 * Pick one active agent and re-queue every selected failed / needs-input
 * task with it (reset to pending), instead of the agent that last worked it.
 * The caller owns the mutation; a success clears the selection, which closes
 * this dialog with the bulk bar.
 */
export function TaskQueueReturnToAgentDialog({
  open,
  onOpenChange,
  taskCount,
  isBusy,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  taskCount: number;
  isBusy: boolean;
  onConfirm: (agent: { id: string; name: string }) => void;
}) {
  const gt = useGT();
  const { data: agents } = useAllAgentDefinitions({ enabled: open });
  const options = buildReturnAgentOptions(agents?.definitions ?? []);
  const [selected, setSelected] = useState<SingleSelectOption | null>(null);

  function close() {
    if (isBusy) return;
    setSelected(null);
    onOpenChange(false);
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (next) onOpenChange(true);
        else close();
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            <T context="dialog title, send selected failed / needs-input tasks to an agent the user picks">
              Return to a different agent
            </T>
          </DialogTitle>
          <DialogDescription>
            {taskCount === 1 ? (
              <T context="dialog description, send one failed / needs-input task to a picked agent">
                Reset this task to pending and queue it for the agent you
                choose.
              </T>
            ) : (
              <T context="dialog description, send several failed / needs-input tasks to a picked agent">
                Reset <Var>{taskCount}</Var> failed or needs-input tasks to
                pending and queue them for the agent you choose.
              </T>
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="task-queue-return-agent">
            <T context="form label, agent to send the selected tasks to">
              Agent
            </T>
          </Label>
          <SingleSelect
            id="task-queue-return-agent"
            options={options}
            value={selected}
            onChange={setSelected}
            placeholder={gt('Select an agent', {
              context: 'placeholder on the return-to-different-agent picker',
            })}
            searchPlaceholder={gt('Search…', {
              context: 'search box in the return-to-different-agent picker',
            })}
            fullWidth
            disabled={isBusy}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={close} disabled={isBusy}>
            <T context="button, cancel return-to-different-agent">Cancel</T>
          </Button>
          <Button
            onClick={() =>
              selected && onConfirm({ id: selected.id, name: selected.label })
            }
            disabled={isBusy || !selected}
          >
            <ArrowsClockwiseIcon className="h-4 w-4" />
            <T context="button, confirm sending the selected tasks to the picked agent">
              Return to agent
            </T>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
